import { useEffect, useState } from "react";

import { businessApi } from "../../../../services/api";
import ScheduleFitNote from "./ScheduleFitNote";

// Report 70 (acceptance gap E): in the hiring setup, how the pay offered fits the work days ticked for the new hire —
// before the setup is approved. Nothing is invented: with no work days the check says so; suggested figures are labelled
// as suggestions and never become the agreed pay. Pay terms are pay data, so the server requires pay permission.

export default function HiringScheduleFit({ baseSalaryCents, salaryFrequency, conversionBasis, agreedDailyRateCents, specialDaysPaid, weeklySchedule, startDate }) {
  const [fit, setFit] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify([baseSalaryCents, salaryFrequency, conversionBasis, agreedDailyRateCents, specialDaysPaid, weeklySchedule, startDate]);

  useEffect(() => {
    if (!baseSalaryCents || baseSalaryCents <= 0) { setFit(null); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      businessApi.previewScheduleFit({
        base_salary_cents: baseSalaryCents,
        salary_frequency: salaryFrequency,
        salary_day_coverage: "SCHEDULED_WORK_DAYS",
        salary_conversion_basis: conversionBasis || undefined,
        agreed_daily_rate_cents: agreedDailyRateCents || undefined,
        special_days_paid: specialDaysPaid === "" ? undefined : specialDaysPaid === "true",
        fixed_salary_confirmed: true,
        weekly_schedule: weeklySchedule || undefined,
        effective_from: startDate || undefined,
      }, { signal: controller.signal })
        .then((res) => setFit(res?.fit || null))
        .catch(() => { if (!controller.signal.aborted) setFit(null); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 400);
    return () => { clearTimeout(timer); controller.abort(); };
    // key carries every input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!baseSalaryCents) return null;
  return (
    <section aria-label="Pay and work days check" className="rounded-panel border border-default bg-surface-sunken p-3 sm:col-span-2" data-testid="hiring-schedule-fit">
      <h3 className="text-label text-secondary">Pay and work days check</h3>
      {fit?.checks?.some((check) => check.code === "no_schedule") ? (
        <p className="mt-1 text-body-sm text-secondary" aria-live="polite">
          No work days ticked, so the pay offered is not compared with a schedule yet. That is fine: set the schedule in HR › Shifts after hiring (payroll waits for it).
        </p>
      ) : (
        <ScheduleFitNote className="mt-1" fit={fit} loading={loading && !fit} />
      )}
      {fit ? <p className="mt-1 text-meta text-tertiary">Advice only. The pay offered is saved as you enter it.</p> : null}
    </section>
  );
}
